import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Modal, Spinner, Table } from "react-bootstrap";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaClock,
  FaEye,
  FaFileAlt,
  FaFileExport,
  FaFolderOpen,
  FaHeadset,
  FaInbox,
  FaSyncAlt,
  FaTasks,
  FaTrophy,
} from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api";
import useMyTicketsDashboard from "../../hooks/useMyTicketsDashboard";
import { statusPillClass, priorityPillClass } from "../tasks/StatusChips";
import DynamicFormRenderer from "../../components/forms/DynamicFormRenderer";
import "../tasks/PortalHomeCards.css";
import "./DeveloperDashboard.css";

// Developer Home redesign: two flat, KRA-grouped sections -- Task KPI
// Performance (evidence_source=TASK_TYPE, auto-calculated from tickets) and
// Form Performance (evidence_source=FORM, read-only summary of Form-based
// feedback). Both come from ONE new, thin endpoint
// (services/developerHomeSummaryService.js) that composes the EXISTING,
// UNMODIFIED /developer/kra-kpi-performance-dashboard response and the
// EXISTING Phase 31 form-evidence resolver -- no new calculation logic
// exists on the frontend OR in that new backend file. MANUAL-evidence KPIs
// never appear here at all -- see the "Manage manual KPI evidence" link,
// the only acknowledgement of their existence on this page (My KPI Evidence
// / KPI Evidence Review own the full workflow).
const HOME_SUMMARY_API = "/developer/home-summary";
const kpiEvidenceApi = (kpiId) => `/developer/kra-kpi-performance-dashboard/kpi/${kpiId}/evidence`;
const formSubmissionDetailApi = (submissionId) => `/developer/form-submissions/${submissionId}/detail`;
const CLOSED_STATUSES = new Set(["completed", "closed", "cancelled"]);

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(value) {
  if (!value) return "";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function monthLabelShort(value) {
  if (!value) return "—";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

function monthLabelShortRange(startValue, endValue) {
  if (!startValue || !endValue) return "";
  const short = (value) => {
    const [year, month] = value.split("-").map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "short" });
  };
  const startYear = startValue.split("-")[0];
  const endYear = endValue.split("-")[0];
  // A period that starts in the selected month is a single month: say
  // "Sept 2026", not "Sept–Sept 2026".
  if (startValue.slice(0, 7) === endValue.slice(0, 7)) return `${short(endValue)} ${endYear}`;
  if (startYear !== endYear) return `${short(startValue)} ${startYear}–${short(endValue)} ${endYear}`;
  return `${short(startValue)}–${short(endValue)} ${endYear}`;
}

// A RATIO_HIGHER_BETTER/RATIO_LOWER_BETTER form KPI (e.g. UAT acceptance
// rate) reports an Accepted/Rejected outcome and a percentage, never a
// "/5"-style rating -- shared by FormPerformanceRow's card figure and
// FormPerformanceDrawer's summary/table so both agree on which KPIs get the
// acceptance-style presentation.
function isRatioCalcType(calculationType) {
  return calculationType === "RATIO_HIGHER_BETTER" || calculationType === "RATIO_LOWER_BETTER";
}

// A COUNT_CUMULATIVE form KPI (e.g. Knowledge Sharing) tallies qualifying
// sessions, not a rating -- its rows never carry a numeric score (see
// kraKpiEvidenceResolverService.js#resolveFormQualifyingSessionCountEvidence,
// which sets score: null by design). FormPerformanceDrawer uses this to swap
// the "Score" column for a "Counted" one so a real, deliberate null doesn't
// read as missing data.
function isCountCumulativeCalcType(calculationType) {
  return calculationType === "COUNT_CUMULATIVE";
}

// Shared by FormPerformanceRow's card figure and FormPerformanceDrawer's
// summary so both agree on the same "X / Y (Z%)" presentation for an
// AVERAGE_SCORE form KPI (e.g. Team Satisfaction Survey's 4.6 / 5) -- a
// RATIO_* KPI already reports its own percentage directly and never goes
// through this helper.
function scorePercent(score, maxScore) {
  if (score == null || !maxScore) return null;
  return Math.round((score / maxScore) * 100);
}

// User Satisfaction audit: a drawer row's own "X / Y (Z%)" cell -- reads the
// resolver's PERSISTED-or-derived obtained_score/maximum_score/percentage
// directly (services/kraKpiEvidenceResolverService.js#resolveFormAverageScoreEvidence),
// never recomputed here. "Not scorable" (never "—") only when this response
// genuinely has no resolvable score at all -- distinct from a real 0.
function formatScoreCell(row) {
  if (row.obtained_score == null || row.maximum_score == null) return "Not scorable";
  const pct = row.percentage != null ? row.percentage : scorePercent(row.obtained_score, row.maximum_score);
  return `${row.obtained_score} / ${row.maximum_score}${pct != null ? ` (${pct}%)` : ""}`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function downloadCsv(filename, headers, rows) {
  const csvContent = [headers, ...rows]
    .map((row) => row.map((value) => `"${String(value ?? "—").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function titleCase(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function isOnTime(row) {
  if (typeof row?.on_time === "boolean") return row.on_time;
  if (!row?.completed_at || !row?.due_at) return null;
  return new Date(row.completed_at) <= new Date(row.due_at);
}

function isOverdue(row) {
  if (!row?.due_at) return false;
  if (CLOSED_STATUSES.has(String(row.status || "").toLowerCase())) return false;
  return new Date(row.due_at) < new Date();
}

function normalizeEvidence(row = {}) {
  return {
    ...row,
    id: row.id ?? row.task_id,
    title: row.title ?? row.task_title ?? "Untitled work",
    project_name: row.project_name ?? row.project?.name ?? "—",
    project_module_name: row.project_module_name ?? row.module_name ?? row.project_module?.name ?? "—",
    task_type_name: row.task_type_name ?? row.task_type?.name ?? row.task_type ?? "—",
  };
}

// Portal Performance is null exactly when no task KPI could be rated this
// scope -- every evidence_source=TASK_TYPE KPI shown here is RATIO_*, never
// COUNT_CUMULATIVE (never legitimately "no monthly rating despite real
// evidence"), so a null score is never a false alarm. Explicit neutral
// state, never 0%/100%.
function portalScoreDisplay(summary) {
  if (summary?.portal_score_percent != null) {
    return { value: `${summary.portal_score_percent}%`, sub: "Score based only on KPIs measurable from tickets." };
  }
  return { value: "Awaiting data", sub: "Not enough eligible portal ticket data for this period yet." };
}

function isEmptySummary(summary) {
  if (!summary) return true;
  const tickets = summary.total_tickets ?? 0;
  const tasks = summary.eligible_evidence_count ?? 0;
  return tickets === 0 && tasks === 0;
}

function AdditionalMeasuresNote({ summary, onManageManualEvidence }) {
  const hasExternal = (summary?.external_configured_weightage ?? 0) > 0;
  if (!hasExternal) return null;
  return (
    <p className="dkra-additional-measures-note">
      Additional performance measures (Manual/Excel evidence) are maintained separately.{" "}
      <button type="button" className="dkra-inline-link" onClick={onManageManualEvidence}>Manage manual KPI evidence</button>
    </p>
  );
}

function SummaryCard({ Icon, label, value, sub, tone = "blue" }) {
  return (
    <div className="dkra-summary-card">
      <div className={`dkra-summary-icon dkra-tone-${tone}`}><Icon /></div>
      <div className="dkra-summary-content">
        <span>{label}</span>
        <strong>{value}</strong>
        {sub ? <small>{sub}</small> : null}
      </div>
    </div>
  );
}

// §8: the User Satisfaction card. Every number here is read verbatim from
// kpi.satisfaction_detail[scope] / kpi[scope] -- never recomputed client-side
// (services/developerKraKpiPerformanceService.js#buildSatisfactionDetail is
// the one source of truth). "Feedback Requests"/"Pending"/"Response Rate"
// only render when auto_requests_sent is a real, non-null, >0 count -- a
// form like the Team Satisfaction Survey has no request-tracking concept at
// all (raters simply choose who to review), so fabricating a request/pending
// figure for it would misrepresent those responses as having a 0-request,
// undefined-rate denominator. Responses/Unique Respondents/Rating Points/
// Average Rating are always shown -- they're computable for any qualifying
// response, from any mapped form.
function SatisfactionCard({ kpi, scope }) {
  if (!kpi || !kpi.satisfaction_detail) return null;
  const d = kpi.satisfaction_detail[scope] || {};
  const metric = kpi[scope] || {};
  const hasAutoTracking = d.auto_requests_sent != null && d.auto_requests_sent > 0;

  return (
    <div className="dkra-satisfaction-card">
      <div className="dkra-side-head">
        <div>
          <span><FaHeadset /> User Satisfaction</span>
          <h4>{kpi.kpi_name}</h4>
        </div>
        {kpi.is_approximation ? <span className="dkra-kpi-status warning">Approximation</span> : null}
      </div>
      <div className="dkra-metric-grid dkra-satisfaction-grid">
        <div><small>Responses Received</small><strong>{d.responses_received ?? 0}</strong></div>
        <div><small>Unique Respondents</small><strong>{d.unique_respondents ?? 0}</strong></div>
        {hasAutoTracking ? (
          <>
            <div><small>Feedback Requests</small><strong>{d.auto_requests_sent}</strong></div>
            <div><small>Pending</small><strong>{d.auto_requests_pending ?? 0}</strong></div>
            <div><small>Response Rate</small><strong>{d.auto_response_rate_percent != null ? `${d.auto_response_rate_percent}%` : "—"}</strong></div>
          </>
        ) : null}
        <div>
          <small>Rating Points</small>
          <strong>{d.rating_points_received != null && d.maximum_possible_points != null ? `${d.rating_points_received} / ${d.maximum_possible_points}` : "—"}</strong>
        </div>
        <div><small>Average Rating</small><strong>{d.average_rating != null ? `${d.average_rating} / 5` : "—"}</strong></div>
        <div><small>KPI Rating</small><strong>{metric.rating_value != null ? `${metric.rating_value} / 5` : "—"}</strong></div>
        <div><small>Contribution</small><strong>{metric.weighted_contribution != null ? `${metric.weighted_contribution} / ${kpi.weightage}` : "—"}</strong></div>
      </div>
      {!hasAutoTracking ? (
        <small className="dkra-metric-evidence">
          Feedback-request tracking applies only to auto-requested task-completion feedback; responses from other sources are counted above without a request/pending figure.
        </small>
      ) : null}
      {d.unattributable_count > 0 ? (
        <small className="dkra-metric-evidence">{d.unattributable_count} response(s) submitted but subject unresolved — excluded above.</small>
      ) : null}
    </div>
  );
}

// Phase 28 (Knowledge Sharing / Mentoring): "Sessions Conducted" is the
// actual KPI metric -- COUNT(DISTINCT form_assignment_id) -- and is read
// verbatim from kpi.session_detail[scope].sessions_conducted (already equal
// to kpi[scope].business_metric_value; never re-derived here). Feedback
// Responses/Unique Respondents describe the SAME period's underlying
// responses without ever being mistaken for extra sessions -- a session
// with 3 respondents is still exactly 1 row in Latest Sessions.
function SessionsCard({ kpi, scope }) {
  if (!kpi || !kpi.session_detail) return null;
  const d = kpi.session_detail[scope] || {};
  const metric = kpi[scope] || {};

  return (
    <div className="dkra-satisfaction-card">
      <div className="dkra-side-head">
        <div>
          <span><FaClipboardList /> Knowledge Sharing &amp; Mentoring</span>
          <h4>{kpi.kpi_name}</h4>
        </div>
        {kpi.is_approximation ? <span className="dkra-kpi-status warning">Approximation</span> : null}
      </div>
      <div className="dkra-metric-grid dkra-satisfaction-grid">
        <div><small>Sessions Conducted</small><strong>{d.sessions_conducted ?? 0}</strong></div>
        <div><small>Feedback Responses</small><strong>{d.feedback_responses ?? 0}</strong></div>
        <div><small>Unique Respondents</small><strong>{d.unique_respondents ?? 0}</strong></div>
        <div><small>KPI Rating</small><strong>{metric.rating_value != null ? `${metric.rating_value} / 5` : "—"}</strong></div>
        <div><small>Contribution</small><strong>{metric.weighted_contribution != null ? `${metric.weighted_contribution} / ${kpi.weightage}` : "—"}</strong></div>
      </div>
      {d.latest_sessions && d.latest_sessions.length ? (
        <div className="dkra-latest-sessions">
          <small className="dkra-metric-label">Latest Sessions</small>
          <ul>
            {d.latest_sessions.map((s) => (
              <li key={s.form_assignment_id}>{s.title || "Untitled session"}{s.session_date ? ` — ${formatDate(s.session_date)}` : ""}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function EvidenceTable({ rows, onView }) {
  if (!rows.length) {
    return (
      <div className="dkra-empty compact">
        <FaInbox />
        <span>No evidence mapped for this KPI in the selected month.</span>
      </div>
    );
  }
  return (
    <div className="unit-table-wrapper dkra-evidence-wrapper">
      <Table responsive className="unit-table tlp-table dkra-evidence-table">
        <thead>
          <tr>
            <th>Evidence / Task</th><th>Project</th><th>Module</th><th>Task Type</th><th>Priority</th>
            <th>Status</th><th className="text-center">Completed</th><th className="text-center">TAT</th><th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((raw, index) => {
            const row = normalizeEvidence(raw);
            const onTime = isOnTime(row);
            return (
              <tr key={row.id ?? `${row.title}-${index}`}>
                <td>
                  <div className="dkra-evidence-title">{row.title}</div>
                  {row.description ? <div className="dkra-evidence-desc">{row.description}</div> : null}
                </td>
                <td>{row.project_name}</td>
                <td>{row.project_module_name}</td>
                <td>{row.task_type_name}</td>
                <td>{row.priority ? <Badge bg={null} className={priorityPillClass(row.priority)}>{titleCase(row.priority)}</Badge> : "—"}</td>
                <td>{row.status ? <Badge bg={null} className={statusPillClass(row.status)}>{titleCase(row.status)}</Badge> : "—"}</td>
                <td className="text-center">{formatDate(row.completed_at || row.created_at)}</td>
                <td className="text-center">
                  {onTime === true ? <span className="dkra-tat good">On Time</span> : null}
                  {onTime === false ? <span className="dkra-tat late">Late</span> : null}
                  {onTime === null && isOverdue(row) ? <span className="dkra-tat late">Overdue</span> : null}
                  {onTime === null && !isOverdue(row) ? <span className="dkra-tat neutral">—</span> : null}
                </td>
                <td className="text-center">
                  {row.id ? (
                    <Button size="sm" variant="outline-primary" onClick={() => onView(row.id)} title="View task"><FaEye /></Button>
                  ) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function formatMetricValue(value, unit) {
  if (value === null || value === undefined) return "—";
  if (unit === "PERCENT") return `${value}%`;
  if (unit === "SCORE_1_5") return `${value} / 5`;
  return value;
}

function rawNumerator(metric) { return metric?.numerator ?? metric?.raw_numerator ?? null; }
function rawDenominator(metric) { return metric?.denominator ?? metric?.raw_denominator ?? null; }
function rawCount(metric) { return metric?.count ?? metric?.raw_count ?? null; }

function formatFormulaResult(metric) {
  const n = rawNumerator(metric);
  const d = rawDenominator(metric);
  if (n != null && d != null) return `${n} / ${d}`;
  const c = rawCount(metric);
  if (c != null) return String(c);
  return "—";
}

// Never "0%"/"0 of N" for a month that's simply not yet evaluated -- an
// explicit, worded neutral state instead. `unit` lives on the KPI itself
// (never inside the monthly/cumulative sub-objects), so it's always passed
// in explicitly rather than read off `metric`.
function taskResultDisplay(metric, unit, { cumulative = false } = {}) {
  if (!metric || metric.source_status === "INSUFFICIENT_DATA") {
    return { value: "Awaiting data", detail: cumulative ? "Not evaluated yet." : "Not enough eligible ticket data for this month." };
  }
  return { value: formatMetricValue(metric.business_metric_value, unit), detail: null };
}

function KpiFormulaModal({ kpi, onClose }) {
  if (!kpi) return null;
  const renderScope = (label, metric) => (
    <div className="mb-3">
      <h6 className="mb-1">{label}</h6>
      {!metric || metric.source_status === "INSUFFICIENT_DATA" ? (
        <div className="dkra-insufficient-data">Automatic calculation unavailable — not enough eligible ticket data yet.</div>
      ) : (
        <Table size="sm" borderless className="mb-0">
          <tbody>
            <tr><td className="text-muted">Result</td><td>{formatFormulaResult(metric)} {metric.business_metric_value != null ? `= ${formatMetricValue(metric.business_metric_value, kpi.unit)}` : ""}</td></tr>
            <tr><td className="text-muted">Rating</td><td>{metric.rating_value != null ? `${metric.rating_value}/5` : "—"}</td></tr>
            <tr><td className="text-muted">Score</td><td>{metric.normalized_score_percent != null ? `${metric.normalized_score_percent}%` : "—"}</td></tr>
            <tr><td className="text-muted">Weight</td><td>{kpi.weightage != null ? `${kpi.weightage}%` : "—"}</td></tr>
            <tr><td className="text-muted">Evidence</td><td>{metric.evidence_count || 0} eligible task(s)</td></tr>
          </tbody>
        </Table>
      )}
    </div>
  );
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>How is this calculated?</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>{kpi.kpi_name || kpi.name}</strong>
          <div className="text-muted small">Formula: {kpi.formula || kpi.business_formula_label || "—"}</div>
          {(kpi.target || kpi.annual_target_label) ? <div className="text-muted small">Target: {kpi.target || kpi.annual_target_label}</div> : null}
        </div>
        {renderScope("This Month", kpi.monthly)}
        {renderScope("Cumulative", kpi.cumulative)}
      </Modal.Body>
    </Modal>
  );
}

// One compact row per task KPI -- name, this-month result, cumulative
// result, evidence count, rating, "View Evidence"/"How is this calculated?"
// actions. Never a nested KRA-card/contribution-panel duplicate of the same
// numbers.
function TaskKpiRow({ kpi, onViewEvidence, onShowFormula }) {
  const monthDisplay = taskResultDisplay(kpi.monthly, kpi.unit);
  const cumulativeDisplay = taskResultDisplay(kpi.cumulative, kpi.unit, { cumulative: true });
  return (
    <div className="dkra-task-kpi-row">
      <div className="dkra-task-kpi-main">
        <div className="dkra-task-kpi-name">
          <strong>{kpi.name}</strong>
          {kpi.is_approximation ? <span className="dkra-kpi-status warning" title="Approximated from the closest available task evidence.">Approximation</span> : null}
        </div>
        <span className="dkra-task-kpi-weightage">Weightage {kpi.weightage != null ? `${kpi.weightage}%` : "—"}</span>
      </div>
      <div className="dkra-task-kpi-figures">
        <div className="dkra-task-kpi-figure">
          <small>This Month</small>
          <strong>{monthDisplay.value}</strong>
          {monthDisplay.detail ? <span className="dkra-task-kpi-figure-note">{monthDisplay.detail}</span> : null}
        </div>
        <div className="dkra-task-kpi-figure">
          <small>Cumulative</small>
          <strong>{cumulativeDisplay.value}</strong>
        </div>
        <div className="dkra-task-kpi-figure">
          <small>Evidence</small>
          <strong>{kpi.cumulative?.evidence_count ?? 0} eligible task(s)</strong>
        </div>
        <div className="dkra-task-kpi-figure">
          <small>Rating</small>
          <strong>{kpi.cumulative?.rating_value != null ? `${kpi.cumulative.rating_value}/5` : "Not evaluated yet"}</strong>
        </div>
      </div>
      <div className="dkra-task-kpi-actions">
        <button type="button" className="dkra-info-icon-btn" title="How is this calculated?" onClick={() => onShowFormula(kpi)}><FaFileAlt /></button>
        <Button size="sm" variant="outline-primary" onClick={() => onViewEvidence(kpi)}>View Evidence</Button>
      </div>
    </div>
  );
}

// KRA shown as a small group label above its task KPIs -- metadata, never a
// card repeating the same numbers.
function TaskKpiSection({ taskKpis, onViewEvidence, onShowFormula }) {
  const byKra = useMemo(() => {
    const groups = [];
    const indexByKra = new Map();
    for (const kpi of taskKpis) {
      if (!indexByKra.has(kpi.kra_id)) {
        indexByKra.set(kpi.kra_id, groups.length);
        groups.push({ kra_id: kpi.kra_id, kra_name: kpi.kra_name, kpis: [] });
      }
      groups[indexByKra.get(kpi.kra_id)].kpis.push(kpi);
    }
    return groups;
  }, [taskKpis]);

  if (!taskKpis.length) {
    return (
      <div className="dkra-empty large">
        <FaTasks />
        <strong>No portal-calculated Task KPIs assigned yet</strong>
        <span>Ask an admin to assign a KRA/KPI framework, and curate which task KPIs show here, to see this section populate.</span>
      </div>
    );
  }
  return (
    <div className="dkra-task-kpi-groups">
      {byKra.map((group) => (
        <div key={group.kra_id} className="dkra-task-kpi-group">
          <div className="dkra-kra-label">{group.kra_name}</div>
          {group.kpis.map((kpi) => (
            <TaskKpiRow key={kpi.kpi_id} kpi={kpi} onViewEvidence={onViewEvidence} onShowFormula={onShowFormula} />
          ))}
        </div>
      ))}
    </div>
  );
}

// §21/§22: the "View Evidence" drawer for a Task KPI -- Month/Cumulative
// toggle, cumulative grouped by month header. Fetches lazily (only once
// opened). Unchanged from before, still reused as-is.
function KpiEvidenceDrawer({ kpi, scope, month, onClose, onScopeChange, onViewTask }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState(null);

  useEffect(() => {
    if (!kpi) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(kpiEvidenceApi(kpi.kpi_id), { params: { scope, month } })
      .then((response) => { if (!cancelled) setEvidence(response?.data?.data || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || err?.message || "Unable to load evidence."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kpi, scope, month]);

  const exportEvidenceCsv = () => {
    const rows = (evidence?.months || []).flatMap((group) => group.tasks || []);
    if (!rows.length) return;
    const headers = ["Evidence / Task", "Project", "Module", "Task Type", "Priority", "Status", "Completed", "TAT"];
    const csvRows = rows.map((raw) => {
      const row = normalizeEvidence(raw);
      const onTime = isOnTime(row);
      const tat = onTime === true ? "On Time" : onTime === false ? "Late" : isOverdue(row) ? "Overdue" : "—";
      return [row.title, row.project_name, row.project_module_name, row.task_type_name, titleCase(row.priority), titleCase(row.status), formatDate(row.completed_at || row.created_at), tat];
    });
    downloadCsv(`${(kpi?.name || "kpi-evidence").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${scope}.csv`, headers, csvRows);
  };

  const hasRows = (evidence?.months || []).some((group) => (group.tasks?.length || 0) > 0);

  return (
    <Modal show={!!kpi} onHide={onClose} scrollable size="lg" dialogClassName="dkra-evidence-modal">
      <Modal.Header closeButton><Modal.Title>{kpi?.name} — Evidence</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="dkra-evidence-toolbar">
          <div className="dkra-evidence-scope-toggle">
            <Button size="sm" variant={scope === "month" ? "primary" : "outline-primary"} onClick={() => onScopeChange("month")}>This Month</Button>
            <Button size="sm" variant={scope === "cumulative" ? "primary" : "outline-primary"} onClick={() => onScopeChange("cumulative")}>Cumulative</Button>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={exportEvidenceCsv} disabled={!hasRows} title="Export evidence as CSV">
            <FaFileExport /> Export CSV
          </Button>
        </div>
        {loading ? (
          <div className="dkra-loading compact"><Spinner animation="border" size="sm" /> Loading evidence…</div>
        ) : error ? (
          <div className="dkra-api-error"><span>{error}</span></div>
        ) : evidence?.months?.length ? (
          evidence.months.map((group) => {
            const hasEvidence = (group.tasks?.length || 0) > 0;
            return (
              <div key={group.period_month} className="dkra-evidence-month-group">
                <h5>{monthLabel(group.period_month)} ({group.tasks?.length || 0})</h5>
                {!hasEvidence ? (
                  <div className="dkra-empty compact">No evidence this month.</div>
                ) : <EvidenceTable rows={group.tasks} onView={onViewTask} />}
                {group.tasks?.[0]?.why_counted ? <p className="dkra-why-counted">{group.tasks[0].why_counted}</p> : null}
              </div>
            );
          })
        ) : (
          <div className="dkra-empty compact"><FaInbox /> No evidence found.</div>
        )}
      </Modal.Body>
    </Modal>
  );
}

// A KPI mapped to more than one form (e.g. a peer Team Satisfaction Survey
// plus the Phase 26 auto-requested technician feedback form both feeding
// the same rating average) sends `form_name` as a " / "-joined string —
// legitimate data, but unreadable run together as one title. Shown here as
// one primary name plus a "+N more" note instead, with the full list still
// available via the title attribute (hover).
function formatFormNames(formName) {
  if (!formName) return { primary: null, extra: null, full: null };
  const names = formName.split(" / ").filter(Boolean);
  return {
    primary: names[0] || null,
    extra: names.length > 1 ? `+${names.length - 1} more form(s)` : null,
    full: formName,
  };
}

function FormStatusBadge({ status }) {
  if (status === "CALCULATED") return <Badge bg="success">Received</Badge>;
  return <Badge bg="secondary">Awaiting responses</Badge>;
}

// One row per Form-evidence KPI -- form name first (primary), KPI/KRA as
// metadata, responses/pending/response-rate/average, "View".
function FormPerformanceRow({ form, scope, onView }) {
  const scoped = scope === "cumulative" ? form.cumulative : form;
  const hasDenominator = scoped.requested_count != null;
  const responsesLabel = hasDenominator
    ? `${scoped.responses_received ?? 0} / ${scoped.requested_count}`
    : `${scoped.responses_received ?? 0}`;
  // The generic "no max_score configured" fallback below is only
  // meaningful for an AVERAGE_SCORE KPI's own rating scale.
  const isRatio = isRatioCalcType(scoped.calculation_type);
  const averagePercent = !isRatio ? scorePercent(scoped.average_score, scoped.max_score) : null;
  const averageLabel = scoped.awaiting_responses
    ? "Awaiting responses"
    : isRatio
      ? (scoped.average_score != null ? `${scoped.average_score}%` : "No acceptance field configured")
      : scoped.average_score != null
        ? `${scoped.average_score}${scoped.max_score ? ` / ${scoped.max_score}` : " / 5"}${averagePercent != null ? ` (${averagePercent}%)` : ""}`
        : scoped.sessions_conducted != null
          ? `${scoped.sessions_conducted} session(s)`
          : "No scorable value";

  const formNames = formatFormNames(form.form_name);

  return (
    <div className="dkra-form-perf-row">
      <div className="dkra-form-perf-main">
        <span className="dkra-form-perf-title-row">
          <strong title={formNames.extra ? formNames.full : undefined}>{formNames.primary || form.kpi_name}</strong>
          {formNames.extra ? <span className="dkra-form-perf-extra" title={formNames.full}>{formNames.extra}</span> : null}
        </span>
        <span className="dkra-form-perf-meta">Related KPI: {form.kpi_name} · {form.kra_name}</span>
      </div>
      <div className="dkra-form-perf-figures">
        <div className="dkra-task-kpi-figure">
          <small>Responses</small>
          <strong>{responsesLabel}</strong>
        </div>
        {hasDenominator ? (
          <>
            <div className="dkra-task-kpi-figure"><small>Pending</small><strong>{scoped.pending_count ?? 0}</strong></div>
            <div className="dkra-task-kpi-figure"><small>Response Rate</small><strong>{scoped.response_rate != null ? `${scoped.response_rate}%` : "—"}</strong></div>
          </>
        ) : null}
        <div className="dkra-task-kpi-figure"><small>Average</small><strong>{averageLabel}</strong></div>
      </div>
      <div className="dkra-task-kpi-actions">
        <FormStatusBadge status={scoped.status} />
        <Button size="sm" variant="outline-primary" onClick={() => onView(form)}>View</Button>
      </div>
    </div>
  );
}

function FormPerformanceSection({ forms, scope, onView }) {
  if (!forms.length) {
    return (
      <div className="dkra-empty large">
        <FaHeadset />
        <strong>No Form-based KPIs mapped yet</strong>
        <span>Once a Form is mapped to one of your KPIs, its responses will summarize here.</span>
      </div>
    );
  }
  return (
    <div className="dkra-form-perf-list">
      {forms.map((form) => <FormPerformanceRow key={form.kpi_id} form={form} scope={scope} onView={onView} />)}
    </div>
  );
}

// One submitted response's complete answer data -- reuses the EXISTING
// DynamicFormRenderer in readOnly mode (never a second form renderer),
// fetched lazily only once a respondent row's "View Response" is clicked.
function SubmissionDetailModal({ submissionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runtime, setRuntime] = useState(null);

  useEffect(() => {
    if (!submissionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(formSubmissionDetailApi(submissionId))
      .then((res) => { if (!cancelled) setRuntime(res?.data?.data || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Unable to load this response."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [submissionId]);

  return (
    <Modal show={!!submissionId} onHide={onClose} scrollable size="lg" dialogClassName="dkra-evidence-modal">
      <Modal.Header closeButton><Modal.Title>Response Detail</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="dkra-loading compact"><Spinner animation="border" size="sm" /> Loading response…</div>
        ) : error ? (
          <div className="dkra-api-error"><span>{error}</span></div>
        ) : runtime ? (
          <>
            {runtime.submission?.obtained_score != null && runtime.submission?.maximum_score != null ? (
              <div className="dkra-form-drawer-summary mb-3">
                <span>Score: {runtime.submission.obtained_score} / {runtime.submission.maximum_score}</span>
                {runtime.submission.percentage != null && <span>Percentage: {runtime.submission.percentage}%</span>}
              </div>
            ) : null}
            <DynamicFormRenderer
              mode="fill"
              readOnly
              sections={runtime.structure?.sections || []}
              values={runtime.valuesByFieldKey || {}}
              repeatingRowsBySection={runtime.repeatingRowsBySection || {}}
              filesByFieldKey={runtime.filesByFieldKey || {}}
              filesByRow={runtime.filesByRow || {}}
              onViewFile={() => {}}
              executionProfile={runtime.executionProfile || null}
            />
          </>
        ) : (
          <div className="dkra-empty compact"><FaInbox /> No response data found.</div>
        )}
      </Modal.Body>
    </Modal>
  );
}

// "View" on a Form Performance row -- Month/Cumulative toggle, a summary
// header, then a respondent-level table. Respondent identity is shown
// exactly as every other form-viewing page in this app already shows it —
// no anonymous-form concept exists anywhere in this codebase today (no
// column, no redaction logic on any page), so there is nothing to redact
// here that isn't already shown elsewhere.
function FormPerformanceDrawer({ form, scope, month, onClose, onScopeChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState(null);
  const [viewingSubmissionId, setViewingSubmissionId] = useState(null);

  useEffect(() => {
    if (!form) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(kpiEvidenceApi(form.kpi_id), { params: { scope, month } })
      .then((response) => { if (!cancelled) setEvidence(response?.data?.data || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Unable to load responses."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [form, scope, month]);

  const scoped = form ? (scope === "cumulative" ? form.cumulative : form) : null;
  const allResponses = (evidence?.months || []).flatMap((g) => g.form_responses || []);
  const formNames = formatFormNames(form?.form_name);
  // Only a RATIO_* form KPI (e.g. UAT acceptance) has a resolvable Project/
  // Module/Result/KPI Score per response -- an AVERAGE_SCORE KPI like Team
  // Satisfaction or a COUNT_CUMULATIVE one like Knowledge Sharing keeps
  // rendering the exact original 3-column table below, completely
  // unchanged, since resolveFormAverageScoreEvidence()/
  // resolveFormQualifyingSessionCountEvidence() were never touched.
  const isRatio = isRatioCalcType(scoped?.calculation_type);
  const isCountCumulative = isCountCumulativeCalcType(scoped?.calculation_type);
  const thisMonthLabel = scope === "month" ? evidence?.months?.[0]?.period_month : null;

  return (
    <>
      <Modal show={!!form} onHide={onClose} scrollable size="lg" dialogClassName="dkra-evidence-modal">
        <Modal.Header closeButton>
          <Modal.Title title={formNames.extra ? formNames.full : undefined}>{formNames.primary || form?.kpi_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dkra-evidence-scope-toggle">
            <Button size="sm" variant={scope === "month" ? "primary" : "outline-primary"} onClick={() => onScopeChange("month")}>This Month</Button>
            <Button size="sm" variant={scope === "cumulative" ? "primary" : "outline-primary"} onClick={() => onScopeChange("cumulative")}>Cumulative</Button>
          </div>
          {scoped ? (
            <div className="dkra-form-drawer-summary">
              <span>KPI: {form?.kpi_name}</span>
              {formNames.extra ? <span title={formNames.full}>{formNames.extra}</span> : null}
              {thisMonthLabel ? <span>KPI Month: {monthLabel(thisMonthLabel)}</span> : null}
              {scoped.requested_count != null ? <span>Requested: {scoped.requested_count}</span> : null}
              <span>Responses: {scoped.responses_received ?? 0}</span>
              {scoped.pending_count != null ? <span>Pending: {scoped.pending_count}</span> : null}
              {scoped.response_rate != null ? <span>Response Rate: {scoped.response_rate}%</span> : null}
              <span>{isRatio ? "Acceptance" : isCountCumulative ? "Sessions Counted" : "Average"}: {scoped.awaiting_responses
                ? "Awaiting responses"
                : isCountCumulative
                  ? `${scoped.sessions_conducted ?? 0} session(s)`
                  : scoped.average_score == null
                    ? "—"
                    : isRatio
                      ? `${scoped.average_score}%`
                      : `${scoped.average_score} / ${scoped.max_score || 5}${scorePercent(scoped.average_score, scoped.max_score || 5) != null ? ` (${scorePercent(scoped.average_score, scoped.max_score || 5)}%)` : ""}`}</span>
            </div>
          ) : null}

          {loading ? (
            <div className="dkra-loading compact"><Spinner animation="border" size="sm" /> Loading responses…</div>
          ) : error ? (
            <div className="dkra-api-error"><span>{error}</span></div>
          ) : allResponses.length ? (
            <div className="unit-table-wrapper dkra-evidence-table-wrapper">
              <Table size="sm" responsive className="unit-table tlp-table">
                {isRatio ? (
                  <>
                    <thead>
                      <tr>
                        <th>Respondent</th>
                        <th>Project</th>
                        <th>Module / Feature</th>
                        <th>KPI Month</th>
                        <th>Submitted Date</th>
                        <th>Result</th>
                        <th>KPI Score</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allResponses.map((row) => (
                        <tr key={row.submission_id}>
                          <td>{row.respondent_name || "—"}</td>
                          <td className="dkra-evidence-wrap">{row.project_name || "—"}</td>
                          <td className="dkra-evidence-wrap">{row.module_name || "—"}</td>
                          <td>{monthLabelShort(row.evidence_month)}</td>
                          <td>{formatDate(row.submitted_at)}</td>
                          <td>{row.result || "—"}</td>
                          <td>{row.obtained_score != null && row.maximum_score != null ? `${row.obtained_score} / ${row.maximum_score}` : "—"}</td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" onClick={() => setViewingSubmissionId(row.submission_id)}>View Response</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead><tr><th>Respondent</th><th>Submitted Date</th><th>{isCountCumulative ? "Counted" : "Score"}</th><th className="text-center">Action</th></tr></thead>
                    <tbody>
                      {allResponses.map((row) => (
                        <tr key={row.submission_id}>
                          <td>{row.respondent_name || "—"}</td>
                          <td>{formatDate(row.submitted_at)}</td>
                          <td>{isCountCumulative ? <Badge bg="success">Session Recorded</Badge> : formatScoreCell(row)}</td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" onClick={() => setViewingSubmissionId(row.submission_id)}>View Response</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </Table>
            </div>
          ) : (
            <div className="dkra-empty compact"><FaInbox /> No responses yet.</div>
          )}
        </Modal.Body>
      </Modal>
      <SubmissionDetailModal submissionId={viewingSubmissionId} onClose={() => setViewingSubmissionId(null)} />
    </>
  );
}

export default function DeveloperDashboard() {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { recentComplaints, complaintsLoading, refresh: refreshExisting } = useMyTicketsDashboard();

  const viewTask = useCallback((id) => {
    navigate(`/${user?.role || "developer"}/tasks/${id}`);
  }, [navigate, user?.role]);

  const [evidenceKpi, setEvidenceKpi] = useState(null);
  const [evidenceScope, setEvidenceScope] = useState("month");
  const [formulaKpi, setFormulaKpi] = useState(null);
  const [formDrawerForm, setFormDrawerForm] = useState(null);
  const [formDrawerScope, setFormDrawerScope] = useState("month");
  const [overviewScope, setOverviewScope] = useState("monthly");
  const autoScopeMonthRef = useRef(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(HOME_SUMMARY_API, { params: { month: selectedMonth } });
      const payload = response.data;
      setData(payload?.data || payload || {});
    } catch (err) {
      setData(null);
      setError(
        err?.response?.data?.message ||
        (err?.response?.status ? `Dashboard API returned ${err.response.status}` : err?.message) ||
        "Unable to load KRA/KPI dashboard data."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleRefresh = async () => {
    await Promise.allSettled([loadDashboard(), Promise.resolve(refreshExisting?.())]);
  };

  const taskKpis = Array.isArray(data?.task_kpis) ? data.task_kpis : [];
  const forms = Array.isArray(data?.forms) ? data.forms : [];
  const monthlySummary = data?.portal_summary?.monthly || {};
  const cumulativeSummary = data?.portal_summary?.cumulative || {};
  const periodStart = data?.period?.start;
  const overviewSummary = overviewScope === "monthly" ? monthlySummary : cumulativeSummary;
  const portalScore = portalScoreDisplay(overviewSummary);
  const monthlySummaryIsEmpty = isEmptySummary(monthlySummary);
  const cumulativeSummaryHasData = !isEmptySummary(cumulativeSummary);

  const initial = (user?.name || "D").trim().charAt(0).toUpperCase();
  const busy = loading || complaintsLoading;

  useEffect(() => {
    if (loading || !data) return;
    if (autoScopeMonthRef.current === selectedMonth) return;
    autoScopeMonthRef.current = selectedMonth;
    if (monthlySummaryIsEmpty && cumulativeSummaryHasData) setOverviewScope("cumulative");
  }, [loading, data, selectedMonth, monthlySummaryIsEmpty, cumulativeSummaryHasData]);

  const openEvidence = useCallback((kpi) => { setEvidenceKpi(kpi); setEvidenceScope("month"); }, []);
  const closeEvidence = useCallback(() => setEvidenceKpi(null), []);
  const openFormDrawer = useCallback((form) => {
    setFormDrawerForm(form);
    setFormDrawerScope(overviewScope === "monthly" ? "month" : "cumulative");
  }, [overviewScope]);

  const goToManualEvidence = useCallback(() => navigate(`/${user?.role || "developer"}/my-kpi-evidence`), [navigate, user?.role]);

  return (
    <div className="unit-page tlp-page dev-dashboard-theme dkra-page">
      <section className="dkra-hero">
        <div className="dkra-hero-user">
          <div className="tlp-hero-avatar">{initial}</div>
          <div>
            <div className="dkra-eyebrow">Developer Home</div>
            <h2>{greeting()}, {user?.name || "Developer"}</h2>
            <p>What's calculated automatically from your tickets, and what Form-based feedback has come in.</p>
          </div>
        </div>
        <div className="dkra-hero-actions">
          <label className="dkra-month-picker">
            <FaCalendarAlt />
            <input type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
          </label>
          <Button className="tlp-btn tlp-btn-ghost" onClick={handleRefresh} disabled={busy} title="Refresh">
            <FaSyncAlt className={busy ? "dkra-spin" : ""} />
          </Button>
        </div>
      </section>

      <div className="dkra-period-strip">
        <div><FaCalendarAlt /><strong>{monthLabel(selectedMonth)}</strong><span>Monthly evidence period</span></div>
        {!loading && !error ? <span className="dkra-live-pill">Live task evidence</span> : null}
      </div>

      {error ? (
        <div className="dkra-api-error">
          <strong>KRA/KPI evidence API is not connected yet.</strong>
          <span>{error}</span>
        </div>
      ) : null}

      <div className="dkra-section-title compact">
        <div>
          <span><FaCalendarAlt /> Performance Overview</span>
          <h3>{overviewScope === "monthly" ? monthLabel(selectedMonth) : periodStart && periodStart.slice(0, 7) === String(selectedMonth).slice(0, 7) ? `Cumulative · ${monthLabel(selectedMonth)}` : `${periodStart ? monthLabel(periodStart) : "April"} – ${monthLabel(selectedMonth)}`}</h3>
        </div>
      </div>
      <div className="dkra-scope-toggle" role="tablist">
        <button type="button" role="tab" className={overviewScope === "monthly" ? "active" : ""} onClick={() => setOverviewScope("monthly")}>{monthLabel(selectedMonth)}</button>
        <button type="button" role="tab" className={overviewScope === "cumulative" ? "active" : ""} onClick={() => setOverviewScope("cumulative")}>Cumulative {periodStart ? monthLabelShortRange(periodStart, selectedMonth) : ""}</button>
      </div>
      {!loading && monthlySummaryIsEmpty && overviewScope === "cumulative" ? (
        <p className="dkra-scope-auto-note">No evidence recorded yet for {monthLabel(selectedMonth)} — showing Cumulative performance instead.</p>
      ) : null}

      <section className="dkra-summary-grid ui-animate-stagger">
        <SummaryCard Icon={FaTrophy} label="Portal Performance" value={loading ? "…" : portalScore.value} sub={loading ? "" : portalScore.sub} tone="blue" />
        <SummaryCard Icon={FaClipboardList} label="Task KPIs Evaluated" value={loading ? "…" : (overviewSummary.eligible_kpi_count ?? 0)} sub="Portal-calculated KPIs curated onto this page" tone="blue" />
        <SummaryCard
          Icon={FaTasks}
          label="Eligible Task Evidence"
          value={loading ? "…" : (overviewSummary.eligible_evidence_count ?? 0)}
          sub={`Across ${overviewSummary.eligible_kpi_count ?? 0} task-based KPI(s)`}
          tone="amber"
        />
        <SummaryCard Icon={FaCheckCircle} label="Completed My Tickets" value={loading ? "…" : (overviewSummary.completed_tickets ?? 0)} sub={`of ${overviewSummary.total_tickets ?? "—"} assigned tickets`} tone="green" />
        <SummaryCard Icon={FaClock} label="On-Time Completed" value={loading ? "…" : (overviewSummary.on_time_completed ?? 0)} sub="Completed within TAT" tone="teal" />
      </section>

      {!loading ? <AdditionalMeasuresNote summary={overviewSummary} onManageManualEvidence={goToManualEvidence} /> : null}

      <div className="dkra-section-title">
        <div><span><FaTasks /> Task KPI Performance</span><h3>Calculated from Ticketing Portal tasks</h3></div>
      </div>
      {loading ? (
        <div className="dkra-loading"><Spinner animation="border" /><span>Loading your task KPI performance…</span></div>
      ) : (
        <TaskKpiSection taskKpis={taskKpis} onViewEvidence={openEvidence} onShowFormula={setFormulaKpi} />
      )}

      <div className="dkra-section-title">
        <div><span><FaHeadset /> Form Performance</span><h3>Form-based KPI feedback</h3></div>
      </div>
      {loading ? (
        <div className="dkra-loading"><Spinner animation="border" /><span>Loading your form performance…</span></div>
      ) : (
        <FormPerformanceSection forms={forms} scope={overviewScope === "monthly" ? "month" : "cumulative"} onView={openFormDrawer} />
      )}

      <section className="dkra-secondary">
        <div className="dkra-section-title compact">
          <div>
            <span><FaHeadset /> Separate from KRA evidence</span>
            <h3>My Recent Complaints</h3>
            <p>IT-support complaints remain separate from your professional KRA/KPI evidence.</p>
          </div>
          <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => navigate("/developer/complaints")}>View All <FaArrowRight /></Button>
        </div>
        {complaintsLoading ? (
          <div className="dkra-loading compact"><Spinner animation="border" size="sm" /> Loading complaints…</div>
        ) : recentComplaints?.length ? (
          <div className="unit-table-wrapper">
            <Table responsive className="unit-table tlp-table">
              <thead><tr><th>Title</th><th>Priority</th><th>Status</th><th>Created</th><th>Due</th><th className="text-center">Action</th></tr></thead>
              <tbody>
                {recentComplaints.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title || "—"}</td>
                    <td>{row.priority ? <Badge bg={null} className={priorityPillClass(row.priority)}>{titleCase(row.priority)}</Badge> : "—"}</td>
                    <td>{row.status ? <Badge bg={null} className={statusPillClass(row.status)}>{titleCase(row.status)}</Badge> : "—"}</td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>{formatDate(row.due_at)}</td>
                    <td className="text-center"><Button size="sm" variant="outline-primary" onClick={() => viewTask(row.id)}><FaEye /></Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="dkra-empty compact"><FaInbox /> No complaints raised.</div>
        )}
      </section>

      <section className="dkra-quickbar ui-animate-stagger">
        <button type="button" onClick={() => navigate("/developer/my-tasks")}> <FaFolderOpen /> <span><b>My Tasks</b><small>View monthly project work</small></span> <FaArrowRight /></button>
        <button type="button" onClick={() => navigate("/developer/tasks/create")}> <FaTasks /> <span><b>Create Ticket</b><small>Add new evidence-generating work</small></span> <FaArrowRight /></button>
      </section>

      <KpiEvidenceDrawer
        kpi={evidenceKpi}
        scope={evidenceScope}
        month={selectedMonth}
        onClose={closeEvidence}
        onScopeChange={setEvidenceScope}
        onViewTask={viewTask}
      />
      {formulaKpi ? <KpiFormulaModal kpi={formulaKpi} onClose={() => setFormulaKpi(null)} /> : null}
      <FormPerformanceDrawer
        form={formDrawerForm}
        scope={formDrawerScope}
        month={selectedMonth}
        onClose={() => setFormDrawerForm(null)}
        onScopeChange={setFormDrawerScope}
      />
    </div>
  );
}
